import { createContext, useContext, useEffect, useState } from "react";

type UserBootstrap = {
  username: string;
  created_at: string;
  name: string | null;
  email: string;
  sex: string | null;
  is_active: boolean;
  completed_intake: boolean;
};

type UserProfilePageData = {
  age_group: string;
  country: string;
  phone_number: string;
  time_zone: string;
  preferred_language: string;
  email_exercise_reminders: boolean;
  email_session_reminders: boolean;
  email_product_updates: boolean;
  email_subscription_reminders: boolean;
  whatsapp_exercise_reminders: boolean;
  whatsapp_session_reminders: boolean;
  website_exercise_reminders: boolean;
  website_session_reminders: boolean;
  website_product_updates: boolean;
  website_subscription_reminders: boolean;
};

type AuthContextType = {
  userId: number | null;
  user: UserBootstrap | null;
  loading: boolean;
  login: (email: string, username: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

async function parseJsonSafely(response: Response) {
  const contentType = response.headers.get("content-type") || "";

  if (!contentType.includes("application/json")) {
    const text = await response.text();
    throw new Error(
      `Expected JSON but got ${contentType || "unknown content type"}: ${text.slice(0, 120)}`
    );
  }

  return response.json();
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [userId, setUserId] = useState<number | null>(null);
  const [user, setUser] = useState<UserBootstrap | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = async () => {
    const res = await fetch("/api/get-platform-data.php", {
      credentials: "include",
      headers: {
        Accept: "application/json",
      },
    });

    if (res.status === 401) {
      setUser(null);
      throw new Error("Not authenticated");
    }

    if (!res.ok) {
      throw new Error(`get-platform-data failed with status ${res.status}`);
    }

    const data = await parseJsonSafely(res);

    if (!data?.data) {
      throw new Error("Invalid get-platform-data response");
    }

    setUser({
      ...data.data,
      is_active: Boolean(Number(data.data.is_active)),
      completed_intake: Boolean(Number(data.data.completed_intake)),
    });
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("/api/api_me.php", {
          credentials: "include",
          headers: {
            Accept: "application/json",
          },
        });

        if (res.status === 401) {
          setUserId(null);
          setUser(null);
          return;
        }

        if (!res.ok) {
          throw new Error(`/api/api_me.php failed with status ${res.status}`);
        }

        const data = await parseJsonSafely(res);

        if (data.success && data.data && data.data.user_id) {
          setUserId(data.data.user_id);

          try {
            await refreshUser();
          } catch (err) {
            console.error("get-platform-data.php failed:", err);
            setUser(null);
          }
        } else {
          setUserId(null);
          setUser(null);
        }
      } catch (e) {
        console.error("Auth bootstrap failed:", e);
        setUserId(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = async (
    email: string,
    username: string,
    password: string
  ): Promise<boolean> => {
    console.warn("auth.login() is not used; login is handled by log-in.php");
    return false;
  };

  const logout = async () => {
    await fetch("/api/api_logout.php", {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
      },
    });

    setUserId(null);
    setUser(null);
  };

  const value: AuthContextType = {
    userId,
    user,
    loading,
    login,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

type UserProfileResponse = {
  success: boolean;
  data?: UserProfilePageData;
  message?: string;
};

export async function getUserProfilePageData(): Promise<UserProfilePageData> {
  const response = await fetch("/api/userprofilepage.php", {
    method: "GET",
    credentials: "include",
    headers: {
      Accept: "application/json",
    },
  });

  if (response.status === 401) {
    throw new Error("Not authenticated");
  }

  const result: UserProfileResponse = await parseJsonSafely(response);

  if (!response.ok || !result.success || !result.data) {
    throw new Error(result.message || "Failed to fetch user profile page data.");
  }

  return result.data;
}

export async function saveUserProfilePageData(profileData: UserProfilePageData) {
  const response = await fetch("/api/userprofilepage-save.php", {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(profileData),
  });

  if (response.status === 401) {
    throw new Error("Not authenticated");
  }

  const result = await parseJsonSafely(response);

  if (!response.ok || !result.success) {
    throw new Error(result.message || "Failed to save user profile page data.");
  }

  return result;
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};